import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface LoadingStateProps {
  type?: 'cards' | 'table' | 'page' | 'list'
  count?: number
  className?: string
}

const SkeletonCard = ({ className, children }: { className?: string; children: React.ReactNode }) => (
  <div className={cn(
    'bg-card rounded-[var(--pd-radius)] border border-border/50 p-6 space-y-4',
    className
  )}>
    {children}
  </div>
)

export function LoadingState({ type = 'page', count = 4, className }: LoadingStateProps) {
  if (type === 'cards') {
    return (
      <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-in fade-in duration-300', className)}>
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonCard key={i} className="p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
          </SkeletonCard>
        ))}
      </div>
    )
  }

  if (type === 'table') {
    return (
      <div className={cn('space-y-1 animate-in fade-in duration-300', className)}>
        <SkeletonCard className="p-0 overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center gap-4 p-4 border-b border-border/50 bg-muted/30">
            <Skeleton className="h-4 w-32 flex-1" />
            <Skeleton className="h-4 w-28 flex-1" />
            <Skeleton className="h-4 w-24 flex-1" />
            <Skeleton className="h-4 w-20 flex-1" />
          </div>
          {/* Table Rows */}
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-border/30 last:border-0">
              <div className="flex items-center gap-3 flex-1">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
              <Skeleton className="h-4 w-24 flex-1" />
              <Skeleton className="h-4 w-20 flex-1" />
              <Skeleton className="h-6 w-20 rounded-full flex-1" />
            </div>
          ))}
        </SkeletonCard>
      </div>
    )
  }

  if (type === 'list') {
    return (
      <div className={cn('space-y-3 animate-in fade-in duration-300', className)}>
        {Array.from({ length: count }).map((_, i) => (
          <SkeletonCard key={i} className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-9 w-9 rounded-lg" />
            </div>
          </SkeletonCard>
        ))}
      </div>
    )
  }

  // Default page skeleton
  return (
    <div className={cn('space-y-6 animate-in fade-in duration-300', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32 rounded-xl" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} className="p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
          </SkeletonCard>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SkeletonCard>
          <Skeleton className="h-5 w-36 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div className="space-y-1 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </SkeletonCard>
        <SkeletonCard>
          <Skeleton className="h-5 w-32 mb-4" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </SkeletonCard>
      </div>
    </div>
  )
}
